<script setup lang="ts">
  import Icon, { IconName } from '@leaflink/stash/Icon.vue';
  import { computed, useCssModule } from 'vue';
  import { type RouteLocationRaw } from 'vue-router';

  export type PostEnrollmentStepActionStatus = 'complete' | 'incomplete' | 'pending';

  // This component is similar to a component called QuickAction in stash.
  // Design did not that want to overly extend QuickAction so we created this component for
  // the speicic PoST Enrollment use-case. If for some reason we determine this component
  // is reusable, we can move it to stash.
  export interface PostEnrollmentStepActionProps {
    /**
     * The title prop will show the first line of text
     */
    title: string;

    /**
     * The subtitle prop will show the second line of text
     */
    subtitle?: string;

    /**
     * The `to` prop for vue-router's `RouterLink` component
     */
    to?: RouteLocationRaw;

    /**
     * If defined, the enrollemnt step action will render as an `<a />` tag.
     */
    href?: string;

    status: PostEnrollmentStepActionStatus;

    /**
     * set to true when the action trigger isn't immediately available
     */
    isLoading?: boolean;
  }

  const props = defineProps<PostEnrollmentStepActionProps>();
  const classes = useCssModule();

  const is = computed(() => {
    if (props.to) {
      return 'router-link';
    }

    if (props.href) {
      return 'a';
    }

    return 'div';
  });

  const linkAttrs = computed(() => {
    if (props.to) {
      return {
        to: props.to,
      };
    }

    if (props.href) {
      return {
        href: props.href,
      };
    }

    return {};
  });

  const statusIcon = computed<IconName>(() => {
    switch (props.status) {
      case 'complete':
        return 'circle-check';
      case 'pending':
        return 'circle-warning';
      case 'incomplete':
        return 'circle-empty';
      default:
        return 'circle-empty';
    }
  });
</script>

<template>
  <component
    :is="is"
    v-bind="linkAttrs"
    class="tw-rounded tw-p-3 tw-border-ice-500 tw-w-full tw-inline-block tw-cursor-pointer"
    :class="[
      [classes.root],
      {
        'tw-bg-white': props.status !== 'complete',
        'tw-pointer-events-none tw-bg-ice-100': props.status !== 'incomplete',
      },
    ]"
    data-test="post-enrollment-step-action"
  >
    <div class="tw-flex tw-items-center tw-gap-3">
      <div class="tw-flex tw-items-center tw-justify-center tw-rounded">
        <Icon
          :data-test="`post-enrollment-step-action|status-${props.status}`"
          :name="statusIcon"
          :class="{
            'tw-text-green-500': props.status === 'complete',
            'tw-text-ice-100': props.status === 'incomplete',
            'tw-text-blue-500': props.status === 'pending',
          }"
        />
      </div>
      <div class="tw-flex-1">
        <div
          :class="{ 'tw-text-ice-700': props.status === 'complete', 'tw-text-ice-900': props.status !== 'complete' }"
        >
          {{ props.title }}
        </div>
        <p v-if="!!props.subtitle" class="tw-text-xs tw-font-normal tw-text-ice-700 lg:tw-mb-0">
          {{ props.subtitle }}
        </p>
      </div>
      <Icon
        v-if="props.status === 'incomplete'"
        data-test="post-enrollment-step-action|arrow"
        :name="props.isLoading ? 'loading-empty' : 'arrow-right'"
        :class="{
          'tw-animate-spin': props.isLoading,
          'tw-text-blue-500': !props.isLoading,
          'tw-text-ice-500': props.isLoading,
        }"
      />
    </div>
  </component>
</template>

<style module>
  .root {
    border: 1px solid var(--color-ice-200); /* it prevents layout size shifting of extra height/width when hover */
  }

  .root:hover,
  .root:active,
  .root:focus {
    border: 1px solid var(--color-blue-500);
    box-shadow: theme('boxShadow.DEFAULT');
    text-decoration: none;
  }
</style>
